import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'

export interface NavigationLink {
  title: string
  href: string
}

export interface NavigationSection {
  title: string
  links: (NavigationLink | NavigationSection)[]
}

export function Navigation({
  className,
  onLinkClick,
  navigationItems,
}: {
  className?: string
  onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>
  navigationItems: NavigationSection[]
}) {
  const { pathname } = useLocation()
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const isInitialMount = useRef(true)

  // Expand default sections only on initial mount
  useEffect(() => {
    const expandDefaults = (sections: NavigationSection[], parentKey = '') => {
      sections.forEach((section) => {
        const sectionKey = parentKey + section.title

        // Always expand "Getting Started" and "Installation" by default
        if (section.title === 'Getting Started') {
          setOpenSections((prev) => ({ ...prev, [sectionKey]: true }))
          const installationSection = section.links.find(
            (item) => 'links' in item && item.title === 'Installation'
          )
          if (installationSection) {
            setOpenSections((prev) => ({ ...prev, [sectionKey + 'Installation']: true }))
          }
        }

        // Expand all top-level troubleshoot sections by default
        if (section.title === 'SDK Guides' || section.title === 'Common Issues' || section.title === 'Integration Help') {
          setOpenSections((prev) => ({ ...prev, [sectionKey]: true }))
        }

        expandDefaults(
          section.links.filter(
            (item): item is NavigationSection => 'links' in item,
          ),
          sectionKey,
        )
      })
    }
    expandDefaults(navigationItems)
  }, [navigationItems])

  // Auto-expand section containing active link on pathname change
  useEffect(() => {
    const expandParents = (sections: NavigationSection[], parentKey = '') => {
      sections.forEach((section) => {
        const sectionKey = parentKey + section.title

        if (
          section.links.some(
            (item) =>
              ('href' in item && item.href === pathname) ||
              ('links' in item &&
                item.links.some(
                  (nested) =>
                    'href' in nested && nested.href === pathname,
                )),
          )
        ) {
          setOpenSections((prev) => ({ ...prev, [sectionKey]: true }))
        }
        expandParents(
          section.links.filter(
            (item): item is NavigationSection => 'links' in item,
          ),
          sectionKey,
        )
      })
    }
    expandParents(navigationItems)
    isInitialMount.current = false
  }, [pathname, navigationItems])

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const renderLinks = (
    links: (NavigationLink | NavigationSection)[],
    parentKey: string,
  ) => (
    <ul
      role="list"
      className="ml-3 mt-2 space-y-2 border-l-2 border-slate-100 lg:mt-4 lg:space-y-4 lg:border-slate-200 dark:border-slate-800"
    >
      {links.map((item) =>
        'href' in item ? (
          <li key={item.href} className="relative">
            <Link
              to={item.href}
              onClick={onLinkClick}
              className={clsx(
                'block w-full pl-3.5 before:pointer-events-none before:absolute before:-left-1 before:top-1/2 before:h-1.5 before:w-1.5 before:-translate-y-1/2 before:rounded-full',
                item.href === pathname
                  ? 'font-semibold text-sky-500 before:bg-sky-500'
                  : 'text-slate-500 before:hidden before:bg-slate-300 hover:text-slate-600 hover:before:block dark:text-slate-400 dark:before:bg-slate-700 dark:hover:text-slate-300',
              )}
            >
              {item.title}
            </Link>
          </li>
        ) : (
          <li key={item.title}>
            <button
              onClick={() => toggleSection(parentKey + item.title)}
              className="flex w-full items-center justify-between pl-3.5 text-left font-display text-sm font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
            >
              {item.title}
              <span
                className={clsx(
                  'ml-2 transition-transform',
                  openSections[parentKey + item.title]
                    ? 'rotate-90'
                    : 'rotate-0',
                )}
              >
                ▶
              </span>
            </button>
            {openSections[parentKey + item.title] &&
              renderLinks(item.links, parentKey + item.title)}
          </li>
        ),
      )}
    </ul>
  )

  return (
    <nav className={clsx('text-base lg:text-sm', className)}>
      <ul role="list" className="space-y-6">
        {navigationItems.map((section) => (
          <li key={section.title}>
            <button
              onClick={() => toggleSection(section.title)}
              className="flex w-full items-center justify-between font-display font-medium text-slate-900 hover:text-slate-700 dark:text-white dark:hover:text-slate-300"
            >
              {section.title}
              <span
                className={clsx(
                  'ml-2 transition-transform',
                  openSections[section.title] ? 'rotate-90' : 'rotate-0',
                )}
              >
                ▶
              </span>
            </button>
            {openSections[section.title] &&
              renderLinks(section.links, section.title)}
          </li>
        ))}
      </ul>
    </nav>
  )
}
